// Putting a conversation back in the shape it arrived in.
//
// The point is not convenience, it is that leaving has to be possible. An
// archive you cannot get out of is a hostage, and the test that matters is
// that our own importer reads what our exporter writes — the same grammar,
// the same attachment markers, the same filenames on both sides.
//
// iOS's shape, because the transcript is `_chat.txt` and that is what an
// iPhone export looks like. The parser reads both, so either would import;
// this one is the honest pairing with the filename.

import type { ParsedMessage } from './parser';
import { mimeForFilename } from './media';
import { writeZip, type ZipInput } from './zipwrite';

const two = (n: number) => String(n).padStart(2, '0');

/** `[23/09/2025, 14:03:12]` — day first, which is what the parser infers. */
function stamp(ts: number): string {
  const d = new Date(ts);
  return `[${two(d.getDate())}/${two(d.getMonth() + 1)}/${d.getFullYear()}, `
    + `${two(d.getHours())}:${two(d.getMinutes())}:${two(d.getSeconds())}]`;
}

const EXT: Record<number, string> = { 2: 'jpg', 3: 'mp4', 4: 'opus', 5: 'bin' };

/**
 * The original filename does not survive ingest — it is dropped once the bytes
 * have a content id, because a name is not what identifies them. So exports
 * carry invented ones. They are stable for a given message, and the transcript
 * and the zip agree, which is all a name has to do here.
 */
function nameFor(m: ParsedMessage, mime: string): string {
  const ext = mime.startsWith('image/') ? (mime.includes('png') ? 'png' : 'jpg')
    : mime.startsWith('video/') ? 'mp4'
    : mime.startsWith('audio/') ? (mime.includes('mpeg') ? 'mp3' : 'opus')
    : EXT[m.kind] ?? 'bin';
  const d = new Date(m.ts);
  return `${String(m.seq).padStart(8, '0')}-${d.getFullYear()}${two(d.getMonth() + 1)}`
    + `${two(d.getDate())}-${two(d.getHours())}${two(d.getMinutes())}${two(d.getSeconds())}.${ext}`;
}

export interface ExportProgress { done: number; total: number }

/**
 * `fetchMedia` is injected rather than assumed: the reading path goes through
 * the Service Worker, and a preview has no Service Worker at all.
 */
export async function exportChat(
  title: string,
  messages: ParsedMessage[],
  fetchMedia: (attach: string) => Promise<{ blob: Blob; mime: string } | null>,
  onProgress?: (p: ExportProgress) => void,
): Promise<{ blob: Blob; filename: string; missing: number }> {
  const files: ZipInput[] = [];
  const lines: string[] = [];
  let missing = 0;

  const withMedia = messages.filter((m) => m.attach);
  let done = 0;

  for (const m of messages) {
    const who = m.sender ? `${m.sender}: ` : '';
    let body = m.body ?? '';

    if (m.attach) {
      const got = await fetchMedia(m.attach).catch(() => null);
      onProgress?.({ done: ++done, total: withMedia.length });
      if (got) {
        const name = nameFor(m, got.mime || mimeForFilename(m.file ?? ''));
        files.push({ name, blob: got.blob, at: new Date(m.ts) });
        // The attachment marker is its own line, then any caption, which is
        // exactly how WhatsApp writes a captioned photo.
        lines.push(`‎${stamp(m.ts)} ${who}‎<attached: ${name}>`);
        if (body) lines.push(body);
        continue;
      }
      // The bytes are gone, so say so in the shape the parser understands
      // rather than dropping the message and quietly shortening the archive.
      missing++;
      body = body || 'image omitted';
    }

    if (m.omitted && !body) body = `${m.omitted} omitted`;
    lines.push(`‎${stamp(m.ts)} ${who}${body}`);
  }

  files.unshift({ name: '_chat.txt', blob: new Blob([lines.join('\n') + '\n'], { type: 'text/plain' }) });

  const safe = title.replace(/[/\\:*?"<>|]/g, '-').trim() || 'Conversation';
  return { blob: await writeZip(files), filename: `WhatsApp Chat - ${safe}.zip`, missing };
}
